// The pop-out camera matrix (#/matrix/:eventId) and the console talk over one BroadcastChannel (same origin, same
// browser). The console says where its player is (camera, mode, playhead) for the event it has open; the matrix plays
// every tile at that time and asks the console to load a camera, go live, sync to the alarm or seek.
//   console → matrix  { t: 'view', eventId, view, at }      on every player change and every 5 s (a heartbeat)
//                     { t: 'event', eventId, version }      the event changed (new alarm, review, close): refetch
//                     { t: 'bye', eventId }                 the console left the event (tab switch, page closed)
//   matrix → console  { t: 'hello', eventId }               "who has this event open?" (answered with a view)
//                     { t: 'cmd', eventId, cmd, … }         load a camera / live / sync / seek / pause
// The console window opens the matrix with window.open under one name, so a second click focuses the same window.
// Phase 5: the pop-out video (#/video/:eventId) is a second listener on the same channel: it shows the console's main
// canvas (its camera, mode and playhead) in its own window, and sends the same commands.

import { useEffect, useRef } from 'react';
import { href } from '../router.ts';
import type { View } from './view.ts';

export const MATRIX_CHANNEL = 'eyeqsoc.matrix';
export const MATRIX_WINDOW = 'eyeqsoc-camera-matrix';
export const VIDEO_WINDOW = 'eyeqsoc-video';
export const HEARTBEAT_MS = 5000;

export type MatrixCmd =
  | { cmd: 'camera'; cameraId: string }
  | { cmd: 'live' }
  | { cmd: 'sync' }
  | { cmd: 'seek'; at: number }
  | { cmd: 'pause'; paused: boolean };

export type MatrixMsg =
  | { t: 'view'; eventId: string; view: View; at: number }
  | { t: 'event'; eventId: string; version: number }
  | { t: 'bye'; eventId: string }
  | { t: 'hello'; eventId: string }
  | ({ t: 'cmd'; eventId: string } & MatrixCmd);

export function openChannel(): BroadcastChannel | null {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(MATRIX_CHANNEL);
  } catch {
    return null;
  }
}

let matrixWin: Window | null = null;
let matrixFor: string | null = null;

/** Opens (or focuses) the camera matrix window for an event, sized for a second monitor to the right. */
export function openMatrix(eventId: string): void {
  const url = `${window.location.pathname}${window.location.search}${href.matrix(eventId)}`;
  if (matrixWin && !matrixWin.closed) {
    if (matrixFor !== eventId) {
      try {
        matrixWin.location.hash = href.matrix(eventId);
      } catch {
        matrixWin = window.open(url, MATRIX_WINDOW);
      }
      matrixFor = eventId;
    }
    matrixWin?.focus();
    return;
  }
  const s = window.screen;
  const w = Math.min(1920, s.availWidth || 1600);
  const h = Math.min(1080, s.availHeight || 900);
  // Right of this window: on a second monitor when there is one (the browser keeps it on screen otherwise).
  const left = Math.round(window.screenX + window.outerWidth);
  const top = Math.max(0, Math.round(window.screenY));
  matrixWin = window.open(url, MATRIX_WINDOW, `popup=yes,width=${w},height=${h},left=${left},top=${top}`);
  matrixFor = eventId;
  matrixWin?.focus();
}

let videoWin: Window | null = null;
let videoFor: string | null = null;

/** Opens (or focuses) the pop-out video window for an event: the main canvas, time-synced, for a second monitor. */
export function openVideo(eventId: string): void {
  const url = `${window.location.pathname}${window.location.search}${href.video(eventId)}`;
  if (videoWin && !videoWin.closed) {
    if (videoFor !== eventId) {
      try {
        videoWin.location.hash = href.video(eventId);
      } catch {
        videoWin = window.open(url, VIDEO_WINDOW);
      }
      videoFor = eventId;
    }
    videoWin?.focus();
    return;
  }
  const w = Math.min(1600, window.screen.availWidth || 1280);
  const h = Math.min(960, window.screen.availHeight || 760);
  const left = Math.round(window.screenX + window.outerWidth);
  const top = Math.max(0, Math.round(window.screenY));
  videoWin = window.open(url, VIDEO_WINDOW, `popup=yes,width=${w},height=${h},left=${left},top=${top}`);
  videoFor = eventId;
  videoWin?.focus();
}

/**
 * The console's side: broadcasts the open event's player and runs the matrix's commands on it.
 * `onCmd` is read through a ref, so it may change every render.
 */
export function useMatrixHost(eventId: string | null, view: View | null, version: number | null, onCmd: (c: MatrixCmd) => void): void {
  const ch = useRef<BroadcastChannel | null>(null);
  const cmdRef = useRef(onCmd);
  cmdRef.current = onCmd;
  const state = useRef<{ eventId: string | null; view: View | null }>({ eventId, view });
  state.current = { eventId, view };

  useEffect(() => {
    const c = openChannel();
    ch.current = c;
    if (!c) return;
    const post = (m: MatrixMsg) => {
      try {
        c.postMessage(m);
      } catch {
        /* closed */
      }
    };
    const sendView = () => {
      const { eventId: id, view: v } = state.current;
      if (id && v) post({ t: 'view', eventId: id, view: v, at: Date.now() });
    };
    c.onmessage = (e: MessageEvent<MatrixMsg>) => {
      const m = e.data;
      if (!m || typeof m !== 'object' || m.eventId !== state.current.eventId) return;
      if (m.t === 'hello') sendView();
      else if (m.t === 'cmd') cmdRef.current(m);
    };
    const beat = setInterval(sendView, HEARTBEAT_MS);
    const bye = () => {
      const id = state.current.eventId;
      if (id) post({ t: 'bye', eventId: id });
    };
    window.addEventListener('pagehide', bye);
    return () => {
      bye();
      clearInterval(beat);
      window.removeEventListener('pagehide', bye);
      c.close();
      ch.current = null;
    };
  }, []);

  // Every player change, and leaving an event (tab switch).
  useEffect(() => {
    if (!eventId || !view) return;
    try {
      ch.current?.postMessage({ t: 'view', eventId, view, at: Date.now() } satisfies MatrixMsg);
    } catch {
      /* closed */
    }
  }, [eventId, view]);
  useEffect(() => {
    if (!eventId) return;
    return () => {
      try {
        ch.current?.postMessage({ t: 'bye', eventId } satisfies MatrixMsg);
      } catch {
        /* closed */
      }
    };
  }, [eventId]);
  useEffect(() => {
    if (!eventId || version === null) return;
    try {
      ch.current?.postMessage({ t: 'event', eventId, version } satisfies MatrixMsg);
    } catch {
      /* closed */
    }
  }, [eventId, version]);
}
